// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Search as SearchIcon, Settings } from '@carbon/icons-react';
import { useSharedSession } from '../../shared/auth/useSharedSession';
import { useSiteSelection } from '../../shared/auth/useSiteSelection';
import styles from './AdminHeader.module.css';

/**
 * Top bar: brand + site switcher + search + current user.
 *
 * The user/site info comes from `useSharedSession()` (shared/), never from
 * Helios' `AuthContext` directly — see admin-center/README.md.
 */
export default function AdminHeader() {
  const navigate = useNavigate();
  const { displayName, initials, isAdmin, siteCount, sites, isLoading } = useSharedSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [siteMenuOpen, setSiteMenuOpen] = useState(false);
  const siteMenuRef = useRef<HTMLDivElement>(null);
  const { selectedSiteId, canSelectFleet, selectSite: setSelectedSiteId } = useSiteSelection();
  const selectedSite = sites.find(site => site.id === selectedSiteId) ?? null;

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
      if (siteMenuRef.current && !siteMenuRef.current.contains(e.target as Node)) {
        setSiteMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <div className={styles.brand}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 265 265"
            fill="none"
            aria-hidden="true"
            className={styles.brandMark}
          >
            <path
              d="M94.84 1.02C93.48 -0.34 91.28 -0.34 89.92 1.02L75.96 14.95C74.6 16.31 74.6 18.51 75.96 19.87L154.73 98.64L173.61 79.79L94.84 1.02ZM263.3 169.51L187.85 94.03L168.97 112.88L244.45 188.36C245.81 189.72 248.01 189.72 249.37 188.36L263.3 174.43C264.66 173.07 264.66 170.87 263.3 169.51ZM173.04 3.28C171.68 1.92 169.48 1.92 168.12 3.28L154.19 17.21C152.83 18.57 152.83 20.77 154.19 22.13L192.73 60.67L211.58 41.82L173.04 3.28ZM261.07 91.31L225.82 56.06L206.97 74.91L242.19 110.16C243.55 111.52 245.75 111.52 247.11 110.16L261.07 96.23C262.43 94.87 262.43 92.67 261.07 91.31ZM19.9 75.96C18.54 74.6 16.34 74.6 14.98 75.96L1.02 89.92C-0.34 91.28 -0.34 93.45 1.02 94.81L79.76 173.55L98.61 154.7L19.9 75.96ZM188.36 244.45L112.88 168.94L94 187.79L169.51 263.3C170.87 264.66 173.07 264.66 174.43 263.3L188.36 249.37C189.72 248.01 189.72 245.81 188.36 244.45ZM97.76 78.55C96.4 77.19 94.2 77.19 92.84 78.55L78.88 92.48C77.52 93.84 77.52 96.04 78.88 97.4L117.39 135.91L136.27 117.06L97.76 78.55ZM185.79 166.58L150.51 131.3L131.66 150.18L166.91 185.43C168.27 186.79 170.47 186.79 171.83 185.43L185.79 171.5C187.15 170.14 187.15 167.94 185.79 166.58ZM22.55 153.77C21.19 152.41 18.99 152.41 17.63 153.77L3.70001 167.7C2.34001 169.06 2.34001 171.26 3.70001 172.62L42.18 211.1L61.03 192.25L22.55 153.77ZM110.58 241.8L75.3 206.52L56.42 225.37L91.7 260.65C93.06 262.01 95.26 262.01 96.62 260.65L110.58 246.72C111.94 245.36 111.94 243.16 110.58 241.8Z"
              fill="url(#adminCenterLogoGrad)"
            />
            <defs>
              <linearGradient
                id="adminCenterLogoGrad"
                x1="65.52"
                y1="66.51"
                x2="236.65"
                y2="234.06"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#FBB584" />
                <stop offset="0.12" stopColor="#F0AB88" />
                <stop offset="0.33" stopColor="#D49195" />
                <stop offset="0.6" stopColor="#A767AB" />
                <stop offset="0.92" stopColor="#692DC8" />
                <stop offset="1" stopColor="#591ED0" />
              </linearGradient>
            </defs>
          </svg>
          <span className={styles.brandText}>Admin Center</span>
        </div>

        <div className={styles.divider} />

        <div ref={siteMenuRef} className={styles.siteMenuAnchor}>
          <button
            type="button"
            className={styles.siteSwitcher}
            onClick={() => setSiteMenuOpen(o => !o)}
            aria-haspopup="menu"
            aria-expanded={siteMenuOpen}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 16 16"
              fill="none"
              className={styles.siteSwitcherIcon}
              aria-hidden="true"
            >
              <path d="M2 3h12M4.5 8h7M6.5 13h3" stroke="currentColor" strokeWidth="1.3" />
            </svg>
            <div className={styles.siteSwitcherText}>
              <div className={styles.siteSwitcherLabel}>{selectedSite?.label ?? 'All sites'}</div>
              <div className={styles.siteSwitcherSub}>
                {isLoading
                  ? 'Loading sites…'
                  : selectedSite
                    ? selectedSite.region
                    : `Fleet · ${siteCount} site${siteCount === 1 ? '' : 's'}`}
              </div>
            </div>
            <ChevronDown size={12} />
          </button>

          {siteMenuOpen && (
            <div className={styles.sitePopover} role="menu">
              <div className={styles.sitePopoverHeader}>Scope · applies everywhere</div>
              {/* selectSite(null) throws for a role that cannot take the fleet scope. */}
              {canSelectFleet && (
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={selectedSiteId === null}
                  className={
                    selectedSiteId === null
                      ? `${styles.siteOption} ${styles.siteOptionActive}`
                      : styles.siteOption
                  }
                  onClick={() => {
                    setSelectedSiteId(null);
                    setSiteMenuOpen(false);
                  }}
                >
                  <div className={styles.siteOptionLabel}>All sites</div>
                  <div className={styles.siteOptionSub}>
                    Fleet · {siteCount} site{siteCount === 1 ? '' : 's'}
                  </div>
                </button>
              )}
              {sites.map(site => (
                <button
                  key={site.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selectedSiteId === site.id}
                  className={
                    selectedSiteId === site.id
                      ? `${styles.siteOption} ${styles.siteOptionActive}`
                      : styles.siteOption
                  }
                  onClick={() => {
                    setSelectedSiteId(site.id);
                    setSiteMenuOpen(false);
                  }}
                >
                  <div className={styles.siteOptionLabel}>{site.label}</div>
                  <div className={styles.siteOptionSub}>{site.region}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={styles.divider} />
      </div>

      <div className={styles.search}>
        <SearchIcon size={15} className={styles.searchIcon} />
        <input type="text" placeholder="Search users, bindings, alarms, facilities" />
      </div>

      <div className={styles.right}>
        <div className={styles.divider} />

        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            className={styles.userBtn}
            onClick={() => setMenuOpen(o => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={displayName ?? undefined}
          >
            <span>{isLoading ? 'Loading…' : (displayName ?? 'Signed in')}</span>
            <span className={styles.avatar} aria-hidden="true">
              {initials ?? '—'}
            </span>
            <ChevronDown size={12} />
          </button>

          {menuOpen && (
            <div className={styles.popover} role="menu">
              <div className={styles.popoverHeader}>
                <div className={styles.popoverAvatar} aria-hidden="true">
                  {initials ?? '—'}
                </div>
                <div>
                  <div className={styles.popoverName}>{displayName ?? 'Signed in'}</div>
                  <div className={styles.popoverSub}>
                    {isAdmin ? 'Platform administrator' : 'Admin Center'}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className={styles.popoverItem}
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/prefs');
                }}
              >
                <span className={styles.popoverItemContent}>
                  <Settings size={16} />
                  Preferences
                </span>
              </button>
              <button
                type="button"
                className={styles.popoverItem}
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/');
                }}
              >
                <span className={styles.popoverItemContent}>
                  <ArrowLeft size={16} />
                  Return to Energy Fluo
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
